import axios from 'axios';
import dayjs from 'dayjs';
import * as types from '../mutation-types';
import type {Connection} from '@/types';
import {push} from 'notivue';

export type TuroConnection = Connection & {
    turo_uuid?: string;
    turo_id?: string | number;
    me?: any;
}

export interface TuroState {
    init: boolean;
    loading: Record<string, boolean>;
    credentials: {
        username: string,
        turo_url?: string,
        password: string,
        token: string,
        method: string,
        challenge_code: string,
        grant_type: string,
        mode: string,
        testing?: boolean
    };
    vehicles: any[];
    allImportedVehicles: any[];
    allVehicles: any[];
    allTuroVehicles: any[];
    currentWorkingTrip: any;
    currentWorkingTripMessages: any[];
    currentWorkingTripImages: any[];
    currentWorkingTripImagesComplete: any[];
    trips: any[];
    emails: any[];
    turoMessageSending: boolean;
    turoGoSet: boolean;
    currentTripTuroGo: any;
    connections: TuroConnection[];
    earnings: any[];
    tripHistory: {
        list: any[],
        numPages: number,
    };
    pendingRequests: any[];
    activityFeed: {
        activities: any[],
    };
    reimbursementDetails: any;
    captchaId: string | null;
    captchaTest: boolean;
    changeRequestSettings: any[];
    feedbackMode: string | null;
    feedback: {
        pending: any[],
        history: any[],
    };
    feedbackPage: {
        pending: number,
        history: number,
    };
    feedbackNextPage: {
        pending: number,
        history: number,
    };
    feedbackTotal: {
        pending: number,
        history: number,
    };
    feedbackPerPage: {
        pending: number,
        history: number,
    };
    feedbackLastPage: {
        pending: number,
        history: number,
    };
    allTuroMessages: any[];
    allTuroMessagesPaginate: any;
    allTuroMessagesCurrentPage: number;
    allTuroMessagesPages: number;
    emailsPagination: any;
}

const state: TuroState = {
    init: false,

    loading: {
        me: false,
        login: false,
        refresh_token: false,
        logout: false,
        trips: false,
        listings: false,
        emails: false,
        messages: false,
        images: false,
        active: false,
        captchaInit: false,
        submitReimbursement: false,
        submitReimbursementWithdraw: false,
        reimbursementDetails: false,
        removeTuro: false,
        loginProcess: false,
        fetch_unavailability: false,
        create_unavailability: false,
        remove_unavailability: false,
        saving_change_request: false,
        update_feedback: false,
        delete_feedback: false,
        match_with_listing: false,
        earnings: false,
        feedback: false,
        vehicles: false,
    },

    turoMessageSending: false,
    credentials: {
        username: '',
        turo_url: '',
        password: '',
        token: '',
        method: 'general',
        challenge_code: '',
        grant_type: 'password',
        mode: 'SMS',
    },
    vehicles: [],
    allImportedVehicles: [],

    allVehicles: [], // Stored on FW
    allTuroVehicles: [], // Fetched straight from Turo

    currentWorkingTrip: {},
    currentWorkingTripMessages: [],
    currentWorkingTripImages: [],
    currentWorkingTripImagesComplete: [],

    trips: [],

    emails: [],

    turoGoSet: false,
    currentTripTuroGo: {
        odometer: {
            scalar: 0,
            unit: '',
        },
    },

    connections: [],
    earnings: [],
    tripHistory: {
        list: [],
        numPages: 0,
    },
    pendingRequests: [],
    activityFeed: {
        activities: [],
    },
    reimbursementDetails: null,
    captchaId: null,
    captchaTest: false,
    changeRequestSettings: [],
    feedbackMode: null,
    feedback: {
        pending: [],
        history: [],
    },
    feedbackPage: {
        pending: 0,
        history: 0,
    },
    feedbackNextPage: {
        pending: 0,
        history: 0,
    },
    feedbackTotal: {
        pending: 0,
        history: 0,
    },
    feedbackPerPage: {
        pending: 0,
        history: 0,
    },
    feedbackLastPage: {
        pending: 0,
        history: 0,
    },
    allTuroMessages: [],
    allTuroMessagesPaginate: {},
    allTuroMessagesCurrentPage: 0,
    allTuroMessagesPages: 1,
    emailsPagination: {},
};

const getters = {
    turoInit: (state: TuroState) => state.init,
    loading: (state: TuroState) => state.loading,
    credentials: (state: TuroState) => state.credentials,
    trips: (state: TuroState) => state.trips,
    currentWorkingTrip: (state: TuroState) => state.currentWorkingTrip,
    currentWorkingTripMessages: (state: TuroState) => state.currentWorkingTripMessages,
    currentWorkingTripImages: (state: TuroState) => state.currentWorkingTripImages,
    allTuroMessages: (state: TuroState) => state.allTuroMessages,
    emails: (state: TuroState) => state.emails,
    vehicles: (state: TuroState) => state.vehicles,
    allTuroVehicles: (state: TuroState) => state.allTuroVehicles,
    currentTripTuroGo: (state: TuroState) => state.currentTripTuroGo,
    connections: (state: TuroState) => state.connections,
    earnings: (state: TuroState) => state.earnings,
    reimbursementDetails: (state: TuroState) => state.reimbursementDetails,
    tripHistory: (state: TuroState) => state.tripHistory,
    pendingRequests: (state: TuroState) => state.pendingRequests,
    activityFeed: (state: TuroState) => state.activityFeed,
    changeRequestSettings: (state: TuroState) => state.changeRequestSettings,
    feedback: (state: TuroState) => state.feedback,
    emailsPagination: (state: TuroState) => state.emailsPagination,
};

const mutations = {
    [types.SET_INIT](state: TuroState, init) {
        state.init = init;
    },
    [types.SET_TURO_LOADING](state: TuroState, loading) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_VEHICLES](state: TuroState, vehicles) {
        state.vehicles = vehicles;
    },
    [types.SET_TURO_EMAILS](state: TuroState, emails) {
        state.emails = emails;
    },
    [types.RESET_VEHICLES](state: TuroState) {
        state.vehicles = [];
    },
    [types.SET_TRIPS](state: TuroState, trips) {
        state.init = true;


        state.trips = trips.map(trip => ({
            key: trip.reservationId,
            renter: trip.actor.firstName,
            listing: trip.vehicle.name,
            time: dayjs(trip.itemTimestamp).format('ddd MMM DD @ H:mm a'),
            make: trip.vehicle.make,
            model: trip.vehicle.model,
            trim: trip.vehicle.trim,
            style: trip.vehicle.style,
            vin: trip.vehicle.vin,
            vin4: trip.vehicle.vin.substring(trip.vehicle.vin.length - 4),
            year: trip.vehicle.year,
            address: trip.address,
            inProgress: trip.inProgress,
            reservationId: trip.reservationId,
            host: trip.host,
            upcomingTripFeedItemType: trip.upcomingTripFeedItemType,
            vehicle: trip.vehicle,
            startF: dayjs(trip.startF).format('ddd MMM DD @ H:mm a'),
            endF: dayjs(trip.endF).format('ddd MMM DD @ H:mm a'),
        }));
    },
    [types.SET_TRIP_SINGLE](state: TuroState, trip) {
        state.init = true;
        state.currentWorkingTrip = trip;
    },
    [types.SET_TURO_ALL_VEHICLES](state: TuroState, vehicles) {
        state.allVehicles = vehicles;
    },
    [types.SET_ALL_IMPORTED_TURO_VEHICLES](state: TuroState, vehicles) {
        state.allImportedVehicles = vehicles;
    },
    [types.SET_TURO_CONNECTIONS](state: TuroState, connections) {
        state.connections = connections;
    },
    [types.SET_TRIP_HISTORY](state: TuroState, history) {
        state.tripHistory = history;
    },
    [types.SET_TRIP_PENDING_REQUESTS](state: TuroState, requests) {
        state.pendingRequests = requests;
    },
    [types.SET_ACTIVITY_FEED](state: TuroState, activity) {
        state.activityFeed = activity;
    },
};

const actions = {

    async createTuroConnection({state, commit}): Promise<{ connected?: boolean, failed412?: any } | undefined> {
        if (state.captchaTest) state.credentials.testing = true;
        if (state.loading.login) {
            return undefined;
        }

        commit('SET_TURO_LOADING', {subject: 'login', loading: true});

        try {
            const response = await axios.post('/v1/connections/turo', state.credentials);
            commit('turo/SET_TURO_CONNECTIONS', response?.data?.company?.connections?.turo ?? [], {root: true});

            return {connected: true};
        } catch (e) {
            if (axios.isAxiosError(e) && e.response?.status === 412) {
                return {failed412: e.response?.data};
            }

            return undefined;
        } finally {
            commit('SET_TURO_LOADING', {subject: 'login', loading: false});
            commit('SET_TURO_LOADING', {subject: 'loginProcess', loading: false});
        }
    },

    /**
     * Toggle a Turo connections active state.
     */
    toggleTuroActive({state, commit}, turo: TuroConnection) {
        commit('SET_TURO_LOADING', {subject: 'active', loading: true});

        const connIndex = state.connections.findIndex(c => turo.uuid === c.uuid);

        if (connIndex !== -1) {
            state.connections[connIndex].loading = true;
        }

        return axios.put(`/v1/connections/turo/${turo.uuid}/toggle`)
            .then(() => {
                if (connIndex !== -1) {
                    state.connections[connIndex].connected = turo.active;
                }

                return {toggled: true, active: Boolean(turo.active)};
            }).catch(e => {
                push.error({
                    message: e?.response?.data?.message || 'There was an error',
                });

                return undefined;
            }).finally(() => {
                commit('SET_TURO_LOADING', {subject: 'active', loading: false});
                if (connIndex !== -1) {
                    state.connections[connIndex].loading = false;
                }
            });
    },


    /**
     * Delete a Turo connection
     */
    removeTuroConnection({commit}, turo) {
        commit('SET_TURO_LOADING', {subject: 'removeTuro', loading: true});

        return axios.delete(`/v1/connections/turo/${turo.uuid}`)
            .then(response => {
                push.success({
                    message: 'Turo connection removed successfully',
                });
                commit('SET_TURO_CONNECTIONS', response?.data?.connections?.turo ?? []);

                return true;
            }).catch(e => {
                console.dir(e);

                return false;
            }).finally(() => {
                commit('SET_TURO_LOADING', {subject: 'removeTuro', loading: false});
            });
    },

    /**
     * Match a Turo vehicle with a Fleetwire listing
     */
    matchVehicleWithFleetwireListing({commit}, data) {
        commit('SET_TURO_LOADING', {subject: 'match_with_listing', loading: true});

        return axios.post('/v1/connections/turo/match-with-listing', data)
            .then(() => {
                return {matched: true};
            }).catch(e => {
                console.dir(e);

                return {error: e?.response};
            }).finally(() => {
                commit('SET_TURO_LOADING', {subject: 'match_with_listing', loading: false});
            });
    },


    fetchTuroEmails({commit}, paginate: {
        page?: number,
        itemsPerPage?: number,
        sortBy?: string,
        sortDesc?: boolean
    } = {}) {
        commit('SET_TURO_LOADING', {subject: 'emails', loading: true});

        if (paginate.page === undefined) {
            paginate.page = 1;
        }

        if (paginate.itemsPerPage === undefined) {
            paginate.itemsPerPage = 10;
        }
        if (paginate.sortBy === undefined) {
            paginate.sortBy = 'created_at';
        }
        if (paginate.sortDesc === undefined) {
            paginate.sortDesc = true;
        }

        axios.get(`/v1/connections/turo/emails?page=${paginate.page}&perPage=${paginate.itemsPerPage}&sortBy=${paginate.sortBy}&sortDesc=${paginate.sortDesc}`)
            .then(response => {
                const emails = response.data.data;

                commit('SET_TURO_EMAILS', emails);
            }).catch(e => {
            console.dir(e);
            if (e.response.status === 400 || e.response.status === 403) {

            }
        }).finally(() => {
            commit('SET_TURO_LOADING', {subject: 'emails', loading: false});
        });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
